import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, inputClass, asList } from './dashboardShared';

// The dashboard's Commands tab: every command TOGΞ has right now, by cog, read from the bot each time it's opened
// (so it follows cogs being loaded, unloaded and changed). Tap one for its full help.

const WHO = {
  everyone: { label: 'Everyone', color: '#23a55a' },
  whatnot: { label: 'Whatnot role', color: '#fbe510' },
  mod: { label: 'Mods', color: '#04aff7' },
  admin: { label: 'Admins', color: '#f0b232' },
  owner: { label: 'Owner', color: '#ff73fa' },
};

export default function CommandsTab({ ask, failed }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [who, setWho] = useState('all');
  const [open, setOpen] = useState('');      // the command showing its full help
  const [closed, setClosed] = useState({});  // cogs folded away
  const [copied, setCopied] = useState('');

  const load = async () => {
    setData(null);
    const answer = await ask('commands');
    if (!answer.ok) { failed(answer); return; }
    // Every list filled in (Firebase drops empty ones, e.g. a command with no aliases), and missing text as ''
    setData({
      prefix: answer.data?.prefix || '?',
      groups: asList(answer.data?.groups).map(g => ({
        cog: g.cog || 'Other', description: g.description || '',
        commands: asList(g.commands).map(c => ({
          name: c.name || '', usage: c.usage || '', short: c.short || '', help: c.help || '',
          aliases: asList(c.aliases), group: !!c.group, who: c.who || 'everyone',
        })),
      })),
    });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase().replace(/^\?/, '');
    return data.groups.map(g => ({
      ...g,
      commands: g.commands.filter(c => (who === 'all' || c.who === who)
        && (!q || c.name.includes(q) || c.aliases.some(a => a.includes(q)) || c.short.toLowerCase().includes(q) || g.cog.toLowerCase().includes(q))),
    })).filter(g => g.commands.length);
  }, [data, search, who]);

  const total = data ? data.groups.reduce((n, g) => n + g.commands.length, 0) : 0;
  const shown = groups.reduce((n, g) => n + g.commands.length, 0);
  const prefix = data?.prefix || '?';

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search commands, e.g. giveaway or reroll" className={`flex-1 ${inputClass}`} />
          <div className="flex gap-1 flex-wrap">
            {[['all', 'All'], ...Object.entries(WHO).map(([k, v]) => [k, v.label])].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setWho(key)}
                className={`px-2 py-1 rounded-md text-[11px] font-semibold ${who === key ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}>{label}</button>
            ))}
            <Button small tone="ghost" onClick={load}>↻</Button>
          </div>
        </div>
        <p className="text-zinc-600 text-[10px] mt-2">
          {data ? `${shown === total ? total : `${shown} of ${total}`} commands in ${groups.length} cogs, read from TOGΞ just now.` : 'Loading…'}
        </p>
      </Card>

      {groups.map(g => (
        <Card key={g.cog} className="!p-0 overflow-hidden">
          <button type="button" onClick={() => setClosed(c => ({ ...c, [g.cog]: !c[g.cog] }))}
            className="w-full flex items-center gap-2 px-3 sm:px-4 py-2.5 text-left hover:bg-zinc-800/40">
            <span className="text-zinc-500 text-[10px]">{closed[g.cog] ? '▶' : '▼'}</span>
            <span className="text-white text-sm font-bold">{g.cog}</span>
            <span className="text-zinc-500 text-[11px] truncate flex-1 min-w-0">{g.description}</span>
            <span className="text-zinc-600 text-[10px]">{g.commands.length}</span>
          </button>
          {!closed[g.cog] && (
            <ul className="divide-y divide-zinc-800 border-t border-zinc-800">
              {g.commands.map(c => {
                const isOpen = open === c.name;
                const badge = WHO[c.who] || WHO.everyone;
                return (
                  <li key={c.name}>
                    <button type="button" onClick={() => setOpen(isOpen ? '' : c.name)}
                      className="w-full text-left px-3 sm:px-4 py-2 hover:bg-zinc-800/30 flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs">
                          <span className="text-white font-bold">{prefix}{c.name}</span>
                          {c.usage && <span className="text-zinc-500"> {c.usage}</span>}
                        </p>
                        <p className="text-zinc-400 text-[11px]">{c.short || <i className="text-zinc-600">No description.</i>}</p>
                      </div>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5"
                        style={{ color: badge.color, background: `${badge.color}22` }}>{badge.label}</span>
                    </button>
                    {isOpen && (
                      <div className="px-3 sm:px-4 pb-3 space-y-2">
                        {c.help && c.help !== c.short && (
                          <pre className="whitespace-pre-wrap break-words text-zinc-300 text-[11px] font-sans bg-zinc-950/60 rounded-lg p-2.5 border border-zinc-800">{c.help.replace(/`/g, '').replace(/\[p\]/g, prefix)}</pre>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <button type="button" onClick={() => {
                            navigator.clipboard?.writeText(`${prefix}${c.name}`).then(() => { setCopied(c.name); setTimeout(() => setCopied(''), 1500); }).catch(() => {});
                          }} className="text-[11px] px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                            {copied === c.name ? 'Copied ✓' : `Copy ${prefix}${c.name}`}
                          </button>
                          {c.aliases.length > 0 && <span className="text-zinc-500 text-[11px]">Also: {c.aliases.map(a => `${prefix}${c.name.split(' ').slice(0, -1).concat(a).join(' ')}`).join(', ')}</span>}
                          {c.group && <span className="text-zinc-600 text-[11px]">Has subcommands (listed below it)</span>}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      ))}
      {data && groups.length === 0 && <Card><p className="text-zinc-500 text-xs">No commands match that.</p></Card>}
    </div>
  );
}
